import {
  Button,
  Card,
  Col,
  Dropdown,
  Form,
  GetRef,
  Image,
  Input,
  InputRef,
  Row,
  Table,
  TableProps,
} from "antd";
import FullPageLayout from "../../../../../templates/FullPageLayout";
import FileDragger from "./Components/FileDragger";
import { useLocation, useNavigate } from "react-router-dom";
import React, { useContext, useEffect, useRef, useState } from "react";
import { RcFile } from "antd/es/upload";
import {
  materialBulkUpload,
  materialBulkUploadCSV,
} from "../../../ducks/services";
import { SuccessMessage } from "../../../../../atoms/Toasts/SuccessMessage";
import { AxiosError, AxiosResponse } from "axios";
import { ErrorMessage } from "../../../../../atoms/Toasts/ErrorMessage";
import Header from "../../../../../atoms/Header";
import { NeedHelp } from "../../../../../atoms/CustomIcons";
import { MoreOutlined } from "@ant-design/icons";

interface FileType {
  name: string;
  type: string;
  size: number;
}

type FormInstance<T> = GetRef<typeof Form<T>>;

const EditableContext = React.createContext<FormInstance<Item> | null>(null);

interface Item {
  key: string;
  name: string;
  cost: number;
}

interface EditableRowProps {
  index: number;
}

const EditableRow: React.FC<EditableRowProps> = ({ index, ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

interface EditableCellProps {
  title: React.ReactNode;
  editable: boolean;
  dataIndex: keyof Item;
  record: Item;
  handleSave: (record: Item) => void;
}

const EditableCell: React.FC<React.PropsWithChildren<EditableCellProps>> = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<InputRef>(null);
  const form = useContext(EditableContext);
  if (!form) {
    throw new Error("EditableContext is not provided.");
  }

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
    }
  }, [editing]);

  const toggleEdit = () => {
    setEditing(!editing);
    form.setFieldsValue({ [dataIndex]: record[dataIndex] });
  };

  const save = async () => {
    try {
      const values = await form.validateFields();

      toggleEdit();
      handleSave({ ...record, ...values });
    } catch (errInfo) {
      console.log("Save failed:", errInfo);
    }
  };

  let childNode = children;

  if (editable) {
    childNode = editing ? (
      <Form.Item
        style={{ margin: 0 }}
        name={dataIndex}
        rules={[{ required: true, message: `${title} is required.` }]}
      >
        <Input ref={inputRef} onPressEnter={save} onBlur={save} />
      </Form.Item>
    ) : (
      <div
        className="editable-cell-value-wrap"
        style={{ paddingInlineEnd: 24 }}
        onClick={toggleEdit}
      >
        {children}
      </div>
    );
  }

  return <td {...restProps}>{childNode}</td>;
};

interface DataType {
  key: React.Key;
  id: string;
  name: string;
  unit: string;
  cost: number;
}

type ColumnTypes = Exclude<TableProps<DataType>["columns"], undefined>;

export default function UploadMaterials() {
  const [checklistFile, setChecklistFile] = useState<FileType | null>(null);
  const [tableRecord, setTableRecord] = useState<DataType | null>(null);
  const [csvData, setCsvData] = useState<[] | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const url = useLocation();
  const companyId = url?.pathname.split("/")[2];

  const submitCsv = () => {
    if (checklistFile) {
      setLoading(true);
      materialBulkUploadCSV({
        company_id: companyId,
        files: checklistFile as File | RcFile,
      })
        .then((res: AxiosResponse) => {
          if (res) {
            console.log("res", res);
            setLoading(false);
            if (res.data) {
              setCsvData(res.data);
            }
          }
        })
        .catch((e: unknown) => {
          if (e instanceof AxiosError) {
            setLoading(false);
            ErrorMessage(
              e?.response?.data?.error?.message ?? "Something went wrong"
            );
          } else {
            setLoading(false);
            ErrorMessage("Something went wrong");
          }
        });
    }
  };

  const saveCsv = () => {
    setLoading(true);
    const payload = dataSource?.map((item) => ({
      name: item?.name,
      cost: item?.cost ? Number(item?.cost) : 0,
      unit: item?.unit,
      company_id: companyId,
    }));

    materialBulkUpload(payload)
      .then(() => {
        setLoading(false);
        SuccessMessage("Materials added");
        navigate(`/inventory/materials/${companyId}`);
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  const [dataSource, setDataSource] = useState<DataType[]>([]);

  useEffect(() => {
    if (csvData && csvData?.length > 0) {
      setDataSource(csvData);
    }
  }, [csvData, setDataSource]);

  const handleDelete = (key: string) => {
    const newData = dataSource.filter((item) => item.id !== key);
    setDataSource(newData);
  };

  const items = [
    {
      key: "1",
      label: (
        <Button
          type="text"
          className="delete-hover"
          icon={<Image src="/images/table/delete-icon.svg" preview={false} />}
          onClick={(e) => {
            e.stopPropagation();
            handleDelete(tableRecord?.id as string);
          }}
        >
          Delete
        </Button>
      ),
    },
  ];

  const defaultColumns: (ColumnTypes[number] & {
    editable?: boolean;
    dataIndex: string;
  })[] = [
    {
      title: "Inventory material name",
      dataIndex: "name",
      width: "488px",
    },
    {
      title: "Unit cost",
      dataIndex: "cost",
      width: "1016px",
      render: (text, record) =>
        text ? (
          <div>
            ${text} <span style={{ paddingLeft: 26 }}>/ {record?.unit}</span>
          </div>
        ) : (
          "-"
        ),
      editable: true,
    },
    {
      title: "",
      dataIndex: "status",
      width: 50,
      render: (_text, record) => (
        <Dropdown
          menu={{
            items,
          }}
          trigger={["click"]}
          placement="bottomRight"
          className="actionButton"
        >
          <Button
            onClick={(e) => {
              console.log("text", record);
              setTableRecord(record);
              e.stopPropagation();
            }}
            onKeyDown={(e) => e.stopPropagation()}
            style={{
              width: "28px",
              height: "28px",
              background: "transparent",
            }}
            className="only_icon"
            icon={<MoreOutlined />}
          />
        </Dropdown>
      ),
    },
  ];

  const handleSave = (row: DataType) => {
    const newData = [...dataSource];
    const index = newData.findIndex((item) => row.key === item.key);
    const item = newData[index];
    newData.splice(index, 1, {
      ...item,
      ...row,
    });
    setDataSource(newData);
  };

  const components = {
    body: {
      row: EditableRow,
      cell: EditableCell,
    },
  };

  const columns = defaultColumns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: DataType) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    };
  });
  return (
    <FullPageLayout loading={loading}>
      {csvData && csvData?.length > 0 ? (
        <div
          className="centered_content"
          style={{ alignItems: "flex-start", padding: "0 24px" }}
        >
          <div className="upload_wrap">
            <Header
              title="Review the prices list and finish import"
              description="Give a final look at the material prices you’re about to update."
              btnText1="Need help?"
              btnIcon1={<NeedHelp />}
            />
            <Table<DataType>
              components={components}
              rowClassName={() => "editable-row"}
              bordered
              dataSource={dataSource}
              columns={columns as ColumnTypes}
              pagination={false}
            />

            <div className="fixed_btm_card">
              <Card>
                <div className="centered">
                  <Button
                    className="outlined-btn"
                    onClick={() => {
                      setCsvData(null);
                      setChecklistFile(null);
                      setDataSource([]);
                    }}
                  >
                    Back
                  </Button>
                  <Button
                    type="primary"
                    className="mint-btn"
                    onClick={() => saveCsv()}
                  >
                    Continue
                  </Button>
                </div>
              </Card>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="centered_content" style={{ maxWidth: "570px" }}>
            <Row>
              <Col>
                <h2>Add materials in bulk</h2>
                <p>
                  Following the instructions below, so you can add a list of
                  materials in one go.
                </p>
                <ol>
                  <li>
                    Create a spreadsheet (CSV) with materials, which must follow
                    the specific format. Click the button below to download a
                    sample:
                    <div style={{ paddingTop: "12px" }}>
                      <Button type="primary" className="mint-btn">
                        Download CSV sample
                      </Button>
                    </div>
                  </li>
                  <li>Upload the prepared file to this field.</li>
                </ol>
                <FileDragger
                  setChecklistFile={setChecklistFile}
                  checklistFile={checklistFile}
                />
              </Col>
            </Row>
          </div>
          <div className="fixed_btm_card">
            <Card>
              <div className="centered">
                <Button
                  className="outlined-btn"
                  onClick={() => navigate(`/add-materials/${companyId}`)}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  className="mint-btn"
                  onClick={() => submitCsv()}
                >
                  Continue
                </Button>
              </div>
            </Card>
          </div>
        </>
      )}
    </FullPageLayout>
  );
}
